import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Building2, User, Mail, Globe2, Users, MapPin, Loader2, Sparkles, X, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// ISO 3166-1 alpha-2 codes; names resolved via Intl so we never hand-maintain them.
const ISO2 =
  "AD AE AF AG AL AM AO AR AT AU AZ BA BB BD BE BF BG BH BI BJ BN BO BR BS BT BW BY BZ CA CD CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GH GM GN GQ GR GT GW GY HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KI KM KN KP KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PT PW PY QA RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TV TZ UA UG US UY UZ VA VC VE VN VU WS YE ZA ZM ZW".split(
    " "
  );

export default function DemoRequestModal({ open, onClose }) {
  const { startSession } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", person_name: "", email: "", employees_count: "", sites_count: "" });
  const [countryIso, setCountryIso] = useState("om");
  const [phone, setPhone] = useState("968");
  const [dialCode, setDialCode] = useState("968");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const countries = useMemo(() => {
    let dn;
    try {
      dn = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      dn = null;
    }
    return ISO2.map((iso) => {
      let name = iso;
      try {
        name = dn ? dn.of(iso) || iso : iso;
      } catch {
        name = iso;
      }
      return { iso: iso.toLowerCase(), name };
    })
      .filter((c) => c.name && c.name !== c.iso.toUpperCase())
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const countryName = countries.find((c) => c.iso === countryIso)?.name || "";

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };

  const onCountryChange = (e) => {
    setCountryIso(e.target.value);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.person_name.trim() || !form.email.trim() || !countryIso) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.employees_count === "" || form.sites_count === "") {
      setError("Please enter the number of employees and sites.");
      return;
    }
    if (!phone || phone.replace(/\D/g, "").length < 6) {
      setError("Please enter a valid mobile number.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await api.post("/auth/demo/request", {
        business_name: form.business_name.trim(),
        person_name: form.person_name.trim(),
        email: form.email.trim().toLowerCase(),
        country: countryName,
        country_code: `+${dialCode}`,
        mobile: `+${phone}`,
        employees_count: parseInt(form.employees_count, 10) || 0,
        sites_count: parseInt(form.sites_count, 10) || 0,
      });
      await startSession(r.access_token);
      toast.success("Your 14-day demo is ready — welcome!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120]" data-testid="demo-request-modal">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 my-4 animate-fade-up overflow-hidden">
            {/* Header */}
            <div className="relative bg-slate-900 px-6 py-6 overflow-hidden">
              <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-indigo-600/30 blur-2xl" />
              <button
                onClick={onClose}
                data-testid="demo-modal-close-btn"
                className="absolute top-4 right-4 text-slate-400 hover:text-white transition"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative flex items-center gap-2 text-indigo-300 text-xs font-bold uppercase tracking-widest">
                <Sparkles className="h-4 w-4" /> 14-Day Free Demo
              </div>
              <h3 className="relative mt-2 font-head text-2xl font-extrabold text-white tracking-tight">
                Request a Demo Account
              </h3>
              <p className="relative mt-1 text-sm text-slate-300">
                Instant full access — unlimited sites &amp; employees. No credit card.
              </p>
            </div>

            {/* Body */}
            <form onSubmit={submit} className="px-6 py-5 space-y-4" data-testid="demo-request-form">
              <IconField
                icon={Building2}
                label="Business Name"
                testid="demo-business-name"
                value={form.business_name}
                onChange={set("business_name")}
                placeholder="Acme Security LLC"
              />
              <IconField
                icon={User}
                label="Your Name"
                testid="demo-person-name"
                value={form.person_name}
                onChange={set("person_name")}
                placeholder="Full name"
              />
              <IconField
                icon={Mail}
                label="Work Email"
                testid="demo-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
              />

              <div className="grid grid-cols-2 gap-4">
                <IconField
                  icon={Users}
                  label="Number of Employees"
                  testid="demo-employees-count"
                  type="number"
                  value={form.employees_count}
                  onChange={(v) => set("employees_count")(v.replace(/\D/g, "").slice(0, 7))}
                  placeholder="e.g. 25"
                />
                <IconField
                  icon={MapPin}
                  label="Number of Sites"
                  testid="demo-sites-count"
                  type="number"
                  value={form.sites_count}
                  onChange={(v) => set("sites_count")(v.replace(/\D/g, "").slice(0, 7))}
                  placeholder="e.g. 3"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Country</label>
                <div className="relative">
                  <Globe2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <select
                    data-testid="demo-country"
                    value={countryIso}
                    onChange={onCountryChange}
                    className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition appearance-none"
                  >
                    {countries.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Mobile Number</label>
                <PhoneInput
                  country={countryIso}
                  value={phone}
                  onChange={(value, data) => {
                    setPhone(value);
                    if (data?.dialCode) setDialCode(data.dialCode);
                    setError("");
                  }}
                  enableSearch
                  countryCodeEditable={false}
                  inputProps={{ "data-testid": "demo-mobile-input", name: "mobile" }}
                  containerClass="!w-full"
                  inputClass="!w-full !h-12 !rounded-xl !border-slate-200 !text-sm !pl-14"
                  buttonClass="!rounded-l-xl !border-slate-200 !bg-slate-50"
                  dropdownClass="!text-sm"
                />
              </div>

              {error && (
                <p
                  className="text-sm font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
                  data-testid="demo-error"
                >
                  {error}
                </p>
              )}

              <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                {["Unlimited Sites", "Unlimited Staff", "Full Feature Access"].map((p) => (
                  <li key={p} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" /> {p}
                  </li>
                ))}
              </ul>

              <button
                type="submit"
                disabled={loading}
                data-testid="demo-submit-btn"
                className="w-full h-12 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Start My Demo <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="text-center text-[11px] text-slate-400">
                We'll email your login details. Your demo stays active for 14 days.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function IconField({ icon: Icon, label, testid, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition"
        />
      </div>
    </div>
  );
}
